import type { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import type { UserUserCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import {
  UserWorkspaceUserCollectionReadModelMaxRole,
  UserWorkspaceUserCollectionReadModelStatus
} from '@r/api/generated/client.schemas.ts';
import type { DataTableFilterField } from '@r/types/index.ts';

type User = UserUserCollectionReadModel;

export const useFilterFields = (): DataTableFilterField<User>[] => {
  const { t } = useTranslation();

  const fields: DataTableFilterField<User>[] = [
    {
      label: t('label.user.firstname'),
      value: 'firstname'
    },
    {
      label: t('label.user.lastname'),
      value: 'lastname'
    },
    {
      label: t('label.user.email'),
      value: 'email'
    },
    {
      label: t('label.user.company'),
      value: 'company' as keyof User
    },
    {
      label: t('label.user.phone'),
      value: 'phone' as keyof User,
      placeholder: t('label.user.phone')
    },
    {
      label: t('label.user.role'),
      value: 'maxRole' as keyof User,
      options: Object.values(UserWorkspaceUserCollectionReadModelMaxRole).map((role) => ({
        label: t(role.toLowerCase().replaceAll('_', '.')),
        value: role
      }))
    },
    {
      label: t('label.company.responsible'),
      value: 'commercial' as keyof User,
      placeholder: t('label.company.responsible')
    },
    {
      label: t('label.user.unique.code'),
      value: 'uniqueCode' as keyof User,
      placeholder: t('label.user.unique.code')
    },
    {
      label: t('label.user.comment'),
      value: 'comment' as keyof User,
      placeholder: t('label.user.comment')
    },
    {
      label: t('label.user.comment2'),
      value: 'comment2' as keyof User,
      placeholder: t('label.user.comment2')
    },
    {
      label: t('label.user.comment3'),
      value: 'comment3' as keyof User,
      placeholder: t('label.user.comment3')
    },
    {
      label: t('label.user.created.at'),
      value: 'createdAt' as keyof User,
      isDateRange: true
    },
    {
      label: t('label.user.registered.at'),
      value: 'registeredAt' as keyof User,
      isDateRange: true
    }
  ];

  const statusEnum = Object.values(UserWorkspaceUserCollectionReadModelStatus);

  if (statusEnum.length > 0) {
    fields.push({
      label: t('label.user.status'),
      value: 'status' as keyof User,
      options: statusEnum.map((status) => ({
        label: t(status),
        value: status
      }))
    });
  }

  return fields;
};

export const useUserColumns = (): ColumnDef<User>[] => {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'id',
      header: t('label.user.id'),
      enableSorting: true
    },
    {
      accessorKey: 'status',
      header: t('label.user.status'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.status ? t(activeWorkspace.status) : '';
      }
    },
    {
      accessorKey: 'firstname',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.user.firstname')} />
      )
    },
    {
      accessorKey: 'lastname',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.user.lastname')} />
      )
    },
    {
      accessorKey: 'email',
      header: t('label.user.email')
    },
    {
      accessorKey: 'phone',
      header: t('label.user.phone'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.phone ?? '';
      }
    },
    {
      accessorKey: 'maxRole',
      header: t('label.user.role'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        const role = activeWorkspace?.maxRole?.toLowerCase().replaceAll('_', '.') ?? '';

        return t(role);
      }
    },
    {
      accessorKey: 'company',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.user.company')} />
      ),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.company?.name;
      }
    },
    {
      id: 'commercial',
      accessorFn: (row) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.userWorkspaces);
        return activeWorkspace?.company?.responsiblesFullName?.[0] ?? '';
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.responsible')} />
      ),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.company?.responsiblesFullName?.join(', ');
      }
    },
    {
      accessorKey: 'uniqueCode',
      header: t('label.user.unique.code'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.uniqueCode;
      }
    },
    {
      id: 'registeredAt',
      accessorFn: (row) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.userWorkspaces);
        return activeWorkspace?.registeredAt ?? '';
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.user.registered.at')} />
      ),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.registeredAt
          ? format(new Date(activeWorkspace.registeredAt as string), 'dd/MM/yyyy', { locale: fr })
          : '';
      }
    },
    {
      accessorKey: 'comment',
      header: t('label.user.comment'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        const comment = activeWorkspace?.comment ?? '';
        return (
          <span className="block max-w-[240px] truncate" title={comment || undefined}>
            {comment}
          </span>
        );
      }
    },
    {
      accessorKey: 'comment2',
      header: t('label.user.comment2'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        const comment2 = activeWorkspace?.comment2 ?? '';
        return (
          <span className="block max-w-[240px] truncate" title={comment2 || undefined}>
            {comment2}
          </span>
        );
      }
    },
    {
      accessorKey: 'comment3',
      header: t('label.user.comment3'),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        const comment3 = activeWorkspace?.comment3 ?? '';
        return (
          <span className="block max-w-[240px] truncate" title={comment3 || undefined}>
            {comment3}
          </span>
        );
      }
    },
    {
      id: 'createdAt',
      accessorFn: (row) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.userWorkspaces);
        return activeWorkspace?.createdAt ?? '';
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.user.created.at')} />
      ),
      cell: ({ row }) => {
        const activeWorkspace = getActiveWorkspaceUniversal(row.original.userWorkspaces);
        return activeWorkspace?.createdAt
          ? format(new Date(activeWorkspace.createdAt), 'dd/MM/yyyy', { locale: fr })
          : '';
      }
    }
  ];
};
